import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type SyntheticEvent } from 'react';

import {
  generalAssistantChat,
  generalAssistantConversation,
  type AiChatMessage,
} from '@/api/assistant';
import {
  AssistantAnswer,
  Badge,
  Button,
  FormField,
  TextArea,
  type AiChatAnswer,
  type AiChatChart,
  type AiChatTable,
} from '@/ui';
import { assistantBlockLabel } from '@/ui/vocabulary';

interface GeneralAssistantProps {
  readonly provider: string;
  readonly identity: string | null;
  /** Shortens the introduction, for a panel that already says who is answering. */
  readonly compact?: boolean;
}

/**
 * One message in the transcript, as the panel holds it.
 *
 * The prose is the message; the table and the chart are what the answer brought
 * with it. They are kept beside the text rather than inside it because that is
 * what the server sends — the live reply carries `table` and `chart` next to
 * `answer`, and a restored turn carries the same object under `structured` —
 * and because a transcript that flattened them into the text would be a
 * transcript that could never change the answer's markup again.
 */
interface ChatEntry {
  readonly role: AiChatMessage['role'];
  readonly content: string;
  readonly table: AiChatTable | null;
  readonly chart: AiChatChart | null;
}

/**
 * Reads a structured answer out of whatever the server sent.
 *
 * The contract carries `table` and `chart` now, so this is one narrowing rather than a cast: an
 * answer without prose is not an answer, and the renderer is handed nothing it cannot draw.
 */
function readAnswer(candidate: AiChatAnswer | null | undefined): AiChatAnswer | null {
  if (candidate === null || candidate === undefined) return null;
  if (typeof candidate.answer !== 'string') return null;
  return {
    answer: candidate.answer,
    table: candidate.table ?? null,
    chart: candidate.chart ?? null,
  };
}

/** The turn's structured answer, if it carries one. */
function structuredOf(turn: AiChatMessage): AiChatAnswer | null {
  return readAnswer(turn.structured ?? null);
}

/** The transcript as the panel holds it, from the server's turns. */
function toEntries(turns: readonly AiChatMessage[]): ChatEntry[] {
  return turns.map((turn) => {
    const structured = structuredOf(turn);
    return {
      role: turn.role,
      content: turn.content,
      table: structured?.table ?? null,
      chart: structured?.chart ?? null,
    };
  });
}

const conversationKey = (identity: string | null): string =>
  `fieldops.general-assistant.conversation.${identity ?? 'signed-out'}`;

function readConversationId(storageKey: string): string | null {
  try {
    return globalThis.localStorage.getItem(storageKey);
  } catch {
    // Private browsing and hardened browser policies can make the Storage API throw even when the
    // property exists. The assistant remains usable because the server owns the transcript.
    return null;
  }
}

function storageReadable(storageKey: string): boolean {
  try {
    globalThis.localStorage.getItem(storageKey);
    return true;
  } catch {
    return false;
  }
}

function writeConversationId(storageKey: string, conversationId: string): boolean {
  try {
    globalThis.localStorage.setItem(storageKey, conversationId);
    return true;
  } catch {
    return false;
  }
}

function removeConversationId(storageKey: string): boolean {
  try {
    globalThis.localStorage.removeItem(storageKey);
    return true;
  } catch {
    return false;
  }
}

/** The free-form assistant mode, backed by a server-owned durable transcript. */
export function GeneralAssistant({
  provider,
  identity,
  compact = false,
}: GeneralAssistantProps): React.JSX.Element {
  const client = useQueryClient();
  const storageKey = conversationKey(identity);
  // The open conversation lives in the query cache, not in component state: the
  // panel can unmount while a reply is in flight, and the reply must still find
  // (and name) the conversation the panel reopens on.
  const conversationIdKey = ['assistant', identity, 'general-conversation-id'];
  const chatKey = ['assistant', identity, 'general-chat'];
  const readStored = (): string | null =>
    identity === null ? null : readConversationId(storageKey);
  const openConversation = useQuery({
    queryKey: conversationIdKey,
    queryFn: readStored,
    initialData: readStored,
    staleTime: Infinity,
  });
  const conversationId = openConversation.data;
  const [localMessages, setLocalMessages] = useState<ChatEntry[]>([]);
  const [draft, setDraft] = useState('');
  const [blocked, setBlocked] = useState<string | null>(null);
  const [storageIssue, setStorageIssue] = useState(
    () => identity !== null && !storageReadable(storageKey),
  );
  const history = useQuery({
    queryKey: ['assistant', identity, 'general-conversation', conversationId],
    queryFn: () => {
      if (conversationId === null) throw new Error('A conversation has not been selected.');
      return generalAssistantConversation(conversationId);
    },
    enabled: identity !== null && conversationId !== null,
  });

  const rememberConversation = (next: string | null): void => {
    client.setQueryData(conversationIdKey, next);
    const stored =
      next === null ? removeConversationId(storageKey) : writeConversationId(storageKey, next);
    if (!stored) setStorageIssue(true);
  };

  // Every callback that must survive the panel unmounting sits on the mutation
  // itself; callbacks passed to mutate() are dropped once the caller is gone.
  const chat = useMutation({
    mutationKey: chatKey,
    mutationFn: ({ message, conversation }: { message: string; conversation: string | null }) =>
      generalAssistantChat({ message, ...(conversation ? { conversationId: conversation } : {}) }),
    onSuccess: (result) => {
      if (result.conversationId) rememberConversation(result.conversationId);
      const response = result.response;
      if (result.status === 'COMPLETED' && response) {
        const structured = readAnswer(response);
        setLocalMessages((current) => [
          ...current,
          {
            role: 'ASSISTANT',
            content: response.answer,
            table: structured?.table ?? null,
            chart: structured?.chart ?? null,
          },
        ]);
      } else {
        setBlocked(
          result.violations.map((violation) => assistantBlockLabel(violation.code)).join(' ') ||
            'The assistant could not answer that request.',
        );
      }
    },
    onSettled: () =>
      client.invalidateQueries({ queryKey: ['assistant', identity, 'general-conversation'] }),
  });
  // Counted across mounts: a panel reopened mid-request must still wait for it.
  const pending = useIsMutating({ mutationKey: chatKey }) > 0;
  const restoring = history.isFetching && localMessages.length === 0;

  const messages: ChatEntry[] =
    localMessages.length > 0 ? localMessages : toEntries(history.data?.messages ?? []);

  function submit(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    const message = draft.trim();
    if (!message || pending) return;
    setDraft('');
    setBlocked(null);
    // Read the previous transcript inside the updater: the cached GET is stale as soon as the
    // first reply lands, so rebuilding from it dropped the exchanges in between.
    setLocalMessages((current) => [
      ...(current.length > 0 ? current : toEntries(history.data?.messages ?? [])),
      { role: 'USER', content: message, table: null, chart: null },
    ]);
    chat.mutate({ message, conversation: conversationId });
  }

  function startNewConversation(): void {
    if (pending) return;
    rememberConversation(null);
    setLocalMessages([]);
    setBlocked(null);
  }
  return (
    <div className="assistant-panel assistant-panel--general">
      <div className="assistant-panel__intro">
        {compact ? null : (
          <Badge tone="ai">{provider === 'fake-local' ? 'Local demonstration' : provider}</Badge>
        )}
        <p>
          {compact
            ? 'Ask about planning, writing, using FieldOps or the schedule. It never changes CRM data.'
            : "Ask for help with planning, writing, learning, or using FieldOps. It can also answer schedule questions from today, tomorrow, or yesterday's read-only dispatch data when your account has access. It never changes CRM data. Your conversation is saved to your account so the assistant can keep its context after a refresh."}
        </p>
      </div>
      {restoring && <p role="status">Restoring your conversation…</p>}
      {history.error && (
        <p role="alert">The saved conversation could not be restored. You can start a new one.</p>
      )}
      {storageIssue && (
        <p role="status">
          This browser cannot remember which conversation to reopen after a refresh. You can keep
          chatting; the server transcript remains the source of truth.
        </p>
      )}
      {messages.length > 0 && (
        <div className="assistant-chat" aria-label="Assistant conversation" aria-live="polite">
          {messages.map((message, index) => (
            <div
              className={`assistant-chat__message assistant-chat__message--${message.role.toLowerCase()}`}
              key={`${message.role}-${String(index)}`}
            >
              <span className="assistant-chat__speaker">
                {message.role === 'USER' ? 'You' : 'Assistant'}
              </span>
              {message.role === 'ASSISTANT' ? (
                // An answer is rendered as UI, never as its own source: markdown
                // as markup, and the table and chart it carried as a table and a
                // chart. A user's message is whatever they typed, verbatim.
                <AssistantAnswer
                  answer={message.content}
                  table={message.table}
                  chart={message.chart}
                />
              ) : (
                <p>{message.content}</p>
              )}
            </div>
          ))}
        </div>
      )}
      {pending && <p role="status">Assistant is thinking…</p>}
      {chat.error && <p role="alert">{chat.error.message}</p>}
      {blocked && <p role="alert">{blocked}</p>}
      <form className="assistant-chat__form" onSubmit={submit}>
        <FormField label="Message the assistant">
          <TextArea
            value={draft}
            maxLength={4000}
            rows={4}
            placeholder="What would you like help with?"
            disabled={pending || restoring}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
        </FormField>
        <div className="assistant-chat__actions">
          <Button type="submit" disabled={!draft.trim() || pending || restoring}>
            {pending ? 'Sending…' : 'Send message'}
          </Button>
          {(messages.length > 0 || conversationId !== null) && (
            <Button type="button" variant="ghost" disabled={pending} onClick={startNewConversation}>
              New conversation
            </Button>
          )}
        </div>
      </form>
    </div>
  );
}
